/**
 * The count thresholds of ONE layer: an and/or tree of like/reply/repost
 * gates, edited in place.
 *
 * Lifted out of the Feed shape unchanged when layers became blocks. Thresholds
 * are an ordering's business — "only posts with at least 5 likes" belongs to
 * the Trending layer, not to the feed as a whole — and the serve path already
 * reads them per lane (`feed_lenses` merges a lane's keys over the feed's).
 * A single-layer feed still writes them to the feed-level key it always did.
 */

import {
  ChevronDownIcon,
  ChevronUpIcon,
  Cross2Icon,
  PlusIcon,
} from "@radix-ui/react-icons";
import { useQuery } from "@tanstack/react-query";
import delve from "dlv";
import { dset } from "dset";
import { klona } from "klona";
import { useState } from "react";

import { BlockContainer } from "@/components/canvas-editor/components/Container";
import {
  CanvasButton,
  CanvasInput,
  CanvasNumberInput,
  CanvasSimpleSelect,
  OperatorSelect,
} from "@/components/canvas-editor/components/Inputs";
import {
  logicalOps,
  numberOperators,
} from "@/components/canvas-editor/shapes/shapes";
import { preventBubble } from "@/components/canvas-editor/utils";
import {
  DropdownMenu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from "@/components/ui/menu";
import {
  ThresholdAuthorLeaf,
  ThresholdFilters,
  ThresholdLeafValue,
  Thresholds,
} from "@/lib/types";
import { cn } from "@/lib/utils";

/** The layer's thresholds editor: the root and/or select plus its rows. */
export function ThresholdsBlock({
  thresholds,
  onChange,
}: {
  thresholds: Thresholds | null | undefined;
  onChange: (thresholds?: Thresholds) => void;
}) {
  const rootLogicalOp = thresholds && "OR" in thresholds ? "OR" : "AND";
  return (
    <div className="sort-area">
      <FeedSortingLogicalBlock
        path={[rootLogicalOp]}
        thresholds={thresholds || { AND: [] }}
        updateThresholds={onChange}
      />
    </div>
  );
}

function FeedSortingLogicalBlock({
  updateThresholds,
  path,
  thresholds,
}: {
  path: string[];
  thresholds: Thresholds;
  updateThresholds: (newThresholds?: Thresholds) => void;
}) {
  const key = path[path.length - 1];

  if (!key) {
    return null;
  }

  const values = delve(thresholds, path) as ThresholdFilters[];
  const onAddThreshold = (
    type:
      | "reply_count"
      | "like_count"
      | "repost_count"
      | "reader_reply_count"
      | "reader_like_count"
      | "reader_repost_count"
      | "request_less_count"
      | "request_more_count",
  ) => {
    const newThresholds = klona(thresholds);
    const index = (delve(newThresholds, path) as ThresholdFilters[]).length;
    dset(newThresholds, [...path, index], { [type]: { op: ">=", value: 0 } });
    updateThresholds(newThresholds);
  };
  const onAddTrustedAuthors = () => {
    const newThresholds = klona(thresholds);
    const index = (delve(newThresholds, path) as ThresholdFilters[]).length;
    const leaf: ThresholdAuthorLeaf = { author_in: { op: "in", value: [] } };
    dset(newThresholds, [...path, index], leaf);
    updateThresholds(newThresholds);
  };
  const onAddThresholdLogic = (type: "AND" | "OR") => {
    const newThresholds = klona(thresholds);
    const index = (delve(newThresholds, path) as ThresholdFilters[]).length;
    dset(newThresholds, [...path, index], { [type]: [] });
    updateThresholds(newThresholds);
  };
  const onMove = (index: number, offset: 1 | -1, root?: boolean) => {
    const newThresholds = klona(thresholds);

    if (index === undefined) return;

    const p = root ? path.slice(0, -2) : path;

    const currentValue = delve(newThresholds, [
      ...p,
      `${index}`,
    ]) as ThresholdLeafValue;
    const switchValue = delve(newThresholds, [
      ...p,
      index + offset,
    ]) as ThresholdLeafValue;

    dset(newThresholds, [...p, index], switchValue);
    dset(newThresholds, [...p, index + offset], currentValue);
    updateThresholds(newThresholds);
  };

  const previousKey = path[path.length - 2];
  const logicIndex = previousKey ? parseInt(previousKey) : undefined;
  const parentValues = previousKey
    ? (delve(thresholds, path.slice(0, -2)) as [])
    : [];

  return (
    // The root row sits inside a layer's body, which brings its own padding;
    // nested groups keep theirs inside their own frame.
    <div className={cn("flex flex-col gap-2", path.length === 1 ? "p-0" : "p-4")}>
      <div className="flex gap-2 pointer-events-auto relative sort-row">
        <CanvasSimpleSelect
          aria-label="Logical Operator"
          value={key?.toLowerCase()}
          placeholder="Type"
          className="flex-1"
          options={logicalOps}
          onValueChange={(v) => {
            const newThresholds = klona(thresholds);
            const realKey = v.toUpperCase();

            if (path.length === 1) {
              delete newThresholds[key as keyof Thresholds];
              dset(newThresholds, realKey, delve(thresholds, path));
            } else {
              const parentPath = path.slice(0, -1);
              dset(newThresholds, parentPath, {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                [realKey]: delve(thresholds, path),
              });
            }

            updateThresholds(newThresholds);
          }}
        />
        <div
          className={cn(
            "hidden sort-area-control absolute top-1/2 -translate-y-1/2 right-0 translate-x-full px-1",
          )}
        >
          <div className="flex gap-1 bg-slate-subtle p-1 rounded-lg border border-slate-normal">
            {logicIndex !== undefined && (
              <>
                <CanvasButton
                  variant="ghost"
                  size="smIcon"
                  onPress={() => onMove(logicIndex, 1, true)}
                  disabled={logicIndex === parentValues.length - 1}
                >
                  <ChevronDownIcon />
                </CanvasButton>
                <CanvasButton
                  variant="ghost"
                  size="smIcon"
                  onPress={() => onMove(logicIndex, -1, true)}
                  disabled={logicIndex === 0}
                >
                  <ChevronUpIcon />
                </CanvasButton>
                <CanvasButton
                  variant="ghost"
                  size="smIcon"
                  onPress={() => {
                    const newThresholds = klona(thresholds);
                    const parentPath = path.slice(0, -2);
                    const newValues = delve(
                      newThresholds,
                      parentPath,
                    ) as ThresholdFilters[];
                    newValues.splice(logicIndex, 1);
                    dset(newThresholds, parentPath, newValues);
                    updateThresholds(newThresholds);
                  }}
                >
                  <Cross2Icon />
                </CanvasButton>
              </>
            )}
            <DropdownMenu>
              <MenuTrigger
                asChild
                className="hidden sort-area-control data-[state=open]:flex"
              >
                <CanvasButton variant="ghost" size="smIcon" isTrigger={true}>
                  <PlusIcon />
                </CanvasButton>
              </MenuTrigger>
              <MenuContent {...preventBubble} sideOffset={12}>
                <MenuItem
                  onSelect={() => {
                    onAddThreshold("reply_count");
                  }}
                >
                  Reply Count
                </MenuItem>
                <MenuItem onSelect={() => onAddThreshold("like_count")}>
                  Like Count
                </MenuItem>
                <MenuItem onSelect={() => onAddThreshold("repost_count")}>
                  Repost Count
                </MenuItem>
                <MenuItem onSelect={() => onAddThreshold("reader_like_count")}>
                  Reader Like Count
                </MenuItem>
                <MenuItem onSelect={() => onAddThreshold("reader_reply_count")}>
                  Reader Reply Count
                </MenuItem>
                <MenuItem
                  onSelect={() => onAddThreshold("reader_repost_count")}
                >
                  Reader Repost Count
                </MenuItem>
                <MenuItem onSelect={() => onAddThreshold("request_less_count")}>
                  Request Less Count
                </MenuItem>
                <MenuItem onSelect={() => onAddThreshold("request_more_count")}>
                  Request More Count
                </MenuItem>
                <MenuSeparator />
                <MenuItem onSelect={onAddTrustedAuthors}>
                  Trusted Authors
                </MenuItem>
                <MenuSeparator />
                <MenuItem onSelect={() => onAddThresholdLogic("AND")}>
                  And
                </MenuItem>
                <MenuItem onSelect={() => onAddThresholdLogic("OR")}>
                  Or
                </MenuItem>
              </MenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      {values &&
        values.length > 0 &&
        values.map((v, index) => {
          const key = Object.keys(v)[0];

          if (!key) {
            return null;
          }

          if (key === "AND" || key === "OR") {
            return (
              <BlockContainer
                variant="amber"
                asDiv={true}
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={`${key}-${index}`}
                className="my-2"
              >
                <FeedSortingLogicalBlock
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={`${key}-${index}`}
                  path={[...path, `${index}`, key]}
                  thresholds={thresholds}
                  updateThresholds={updateThresholds}
                />
              </BlockContainer>
            );
          }

          if (key === "author_in") {
            const leaf = delve(thresholds, [
              ...path,
              index,
              key,
            ]) as ThresholdAuthorLeaf["author_in"];
            return (
              <TrustedAuthorsRow
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={`${key}-${index}`}
                dids={leaf.value ?? []}
                onChange={(dids) => {
                  const newThresholds = klona(thresholds);
                  dset(newThresholds, [...path, index, key, "value"], dids);
                  updateThresholds(newThresholds);
                }}
                onRemove={() => {
                  const newThresholds = klona(thresholds);
                  const newValues = delve(
                    newThresholds,
                    path,
                  ) as ThresholdFilters[];
                  newValues.splice(index, 1);
                  dset(newThresholds, path, newValues);
                  updateThresholds(newThresholds);
                }}
              />
            );
          }

          const data = delve(thresholds, [
            ...path,
            index,
            key,
          ]) as ThresholdLeafValue;

          return (
            <div
              // eslint-disable-next-line @eslint-react/no-array-index-key
              key={`${key}-${index}`}
              className="pointer-events-auto flex flex-col gap-2 relative sort-row"
            >
              <div className="flex gap-2">
                <CanvasSimpleSelect
                  aria-label="Field"
                  value={key}
                  className="w-1/3"
                  options={[
                    "like_count",
                    "reply_count",
                    "repost_count",
                    "reader_like_count",
                    "reader_reply_count",
                    "reader_repost_count",
                    "request_less_count",
                    "request_more_count",
                  ]}
                  onValueChange={(v) => {
                    const newThresholds = klona(thresholds);
                    const old = delve(newThresholds, [
                      ...path,
                      index,
                    ]) as Record<string, unknown>;

                    delete old[key];
                    dset(newThresholds, [...path, index, v], data);
                    updateThresholds(newThresholds);
                  }}
                />
                <OperatorSelect
                  value={data.op}
                  validOperators={numberOperators}
                  onValueChange={(v) => {
                    const newThresholds = klona(thresholds);
                    dset(newThresholds, [...path, index, key, "op"], v);
                    updateThresholds(newThresholds);
                  }}
                />
                <CanvasNumberInput
                  placeholder="Threshold"
                  className="flex-1"
                  value={data.value}
                  min={0}
                  onValueChange={(v) => {
                    const newThresholds = klona(thresholds);
                    dset(newThresholds, [...path, index, key, "value"], v);
                    updateThresholds(newThresholds);
                  }}
                />

                <div className="absolute top-1/2 -translate-y-1/2 right-0 translate-x-full px-1">
                  <div className="hidden sort-area-control gap-1 bg-slate-subtle p-1 rounded-lg border border-slate-normal">
                    <CanvasButton
                      variant="ghost"
                      size="smIcon"
                      onPress={() => onMove(index, 1)}
                      disabled={index === values.length - 1}
                    >
                      <ChevronDownIcon />
                    </CanvasButton>
                    <CanvasButton
                      variant="ghost"
                      size="smIcon"
                      onPress={() => onMove(index, -1)}
                      disabled={index === 0}
                    >
                      <ChevronUpIcon />
                    </CanvasButton>
                    <CanvasButton
                      variant="ghost"
                      size="smIcon"
                      onPress={() => {
                        const newThresholds = klona(thresholds);
                        const newValues = delve(
                          newThresholds,
                          path,
                        ) as ThresholdFilters[];
                        newValues.splice(index, 1);
                        dset(newThresholds, path, newValues);
                        updateThresholds(newThresholds);
                      }}
                    >
                      <Cross2Icon />
                    </CanvasButton>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
}


const APPVIEW = "https://public.api.bsky.app/xrpc";

async function resolveAuthor(input: string): Promise<string> {
  const value = input.trim().replace(/^@/, "");
  if (value.startsWith("did:")) return value;
  const response = await fetch(
    `${APPVIEW}/com.atproto.identity.resolveHandle?handle=${encodeURIComponent(value)}`,
  );
  if (!response.ok) throw new Error(`Could not find @${value}`);
  return ((await response.json()) as { did: string }).did;
}

/** Handles for the DIDs we store, so the row reads as people rather than hashes. */
function useHandles(dids: string[]) {
  return useQuery({
    queryKey: ["threshold-author-handles", dids],
    enabled: dids.length > 0,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const handles: Record<string, string> = {};
      for (let i = 0; i < dids.length; i += 25) {
        const params = new URLSearchParams();
        for (const did of dids.slice(i, i + 25)) params.append("actors", did);
        const response = await fetch(
          `${APPVIEW}/app.bsky.actor.getProfiles?${params.toString()}`,
        );
        if (!response.ok) continue;
        const body = (await response.json()) as {
          profiles: { did: string; handle: string }[];
        };
        for (const p of body.profiles) handles[p.did] = p.handle;
      }
      return handles;
    },
  });
}

/**
 * `author_in`: posts by these accounts pass whatever their counts. Stored as DIDs so a
 * handle change cannot silently drop a trusted source; typed as handles, because nobody
 * knows a DID by heart.
 */
function TrustedAuthorsRow({
  dids,
  onChange,
  onRemove,
}: {
  dids: string[];
  onChange: (dids: string[]) => void;
  onRemove: () => void;
}) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const { data: handles } = useHandles(dids);

  const add = async () => {
    const entries = draft
      .split(/[\s,]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    if (entries.length === 0) return;
    setIsAdding(true);
    setError(null);
    const added: string[] = [];
    const failed: string[] = [];
    for (const entry of entries) {
      try {
        added.push(await resolveAuthor(entry));
      } catch {
        failed.push(entry);
      }
    }
    setIsAdding(false);
    if (added.length > 0) onChange([...new Set([...dids, ...added])]);
    setDraft(failed.join(" "));
    if (failed.length > 0) setError(`Could not find: ${failed.join(", ")}`);
  };

  return (
    <div className="pointer-events-auto flex flex-col gap-2 relative sort-row">
      <div className="text-xs text-slate-dim">
        Posts by these accounts skip the other thresholds in this group. Put
        them in an <b>Or</b> beside a count to let trusted sources straight in.
      </div>
      <div className="flex gap-2">
        <CanvasInput
          aria-label="Add trusted authors"
          placeholder="handle.bsky.social, did:plc:…"
          className="flex-1"
          value={draft}
          onValueChange={setDraft}
        />
        <CanvasButton
          variant="outline"
          size="sm"
          isLoading={isAdding}
          onPress={() => void add()}
        >
          Add
        </CanvasButton>
        <CanvasButton variant="ghost" size="smIcon" onPress={onRemove}>
          <Cross2Icon />
        </CanvasButton>
      </div>
      {error && <div className="text-xs text-red-11">{error}</div>}
      {dids.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {dids.map((did) => (
            <span
              key={did}
              className="flex items-center gap-1 rounded-md border border-slate-normal px-2 py-0.5 text-xs"
            >
              {handles?.[did] ? `@${handles[did]}` : did}
              <button
                type="button"
                aria-label={`Remove ${handles?.[did] ?? did}`}
                className="text-slate-dim hover:text-slate-12"
                onClick={() => onChange(dids.filter((d) => d !== did))}
              >
                <Cross2Icon />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <div className="text-xs text-slate-dim">
          No authors yet — an empty list exempts nobody.
        </div>
      )}
    </div>
  );
}
